<template>
	<!--
		THE SLIP ON PAPER - THE LAYOUT, IN VUE

		The paper used to be assembled by the **shell** (`device/slip.ts`) out of strings, so the
		layout could only change with a new release of the shell - an update of every machine in every
		branch because a line moved. It stands here now and changes with a deploy of the web, and it
		is drawn by a real page (`views/print/PrintSlip.vue`) that both the shell and a browser load.

		WHAT IS ALLOWED HERE AND WHAT IS NOT

		- **Interpolation only**, never `v-html`: Vue escapes interpolation, and that is what keeps
		  the paper safe from text somebody put into a shop name or a company header along the way.
		- **Nothing from outside**: no images by address, no fonts, no `@import`. The print window has
		  a lock that admits only its own origin (`device/printWindow.ts`), and a printer waiting on a
		  network is a printer that prints nothing. The logo therefore arrives as a `data:` URI, with
		  the data.
		- **The barcode is drawn by this page**, and that is a change: the shell used to insert it, so
		  paper printed from a browser came out with an empty frame in its place - the code then could
		  not be scanned, only copied by hand. There is one source (`lib/shared/Code128.ts`,
		  `lib/shared/QrCode.ts`, imported directly), and the shell's copy is only for its fallback paper.
	-->
	<div class="slip">
		<!--
			THE STYLE STANDS BESIDE THE MARKUP, AND THAT IS A FIX RATHER THAN TIDINESS

			The first version of this component had markup only - the classes lived in
			`device/slip.ts`, hence in another release. Renaming a class here then **silently** stops
			taking effect, and that is found out on paper.

			`<component :is="'style'">` and not an SFC `<style>` block: that one is extracted at build
			time into the application's CSS file, so it would not end up in the `innerHTML` that goes
			to the shell by the fallback path. The same move already stands in `LottoHeader.vue`.
		-->
		<component :is="'style'">{{ css }}</component>

		<!--
			TVRTKA ČIJI JE LISTIĆ

			Postavka po operaciji (`retail.slip`, `lib/SlipBranding`) — dvije operacije u istom procesu
			su dvije tvrtke. Bez postavke se ne crta ništa i papir počinje kod imena igre.
		-->
		<div v-if="hasBranding" class="brand">
			<img v-if="slip.branding.logo" class="logo" :src="slip.branding.logo" alt="">
			<p v-if="slip.branding.header" class="head">{{ slip.branding.header }}</p>
		</div>

		<!--
			TESTNI LISTIĆ TO KAŽE PRVI, PRIJE SVEGA OSTALOG

			Izgleda kao pravi, pa je prvi put kad ga netko donese na blagajnu ujedno i trenutak u kojem
			se mora odbiti papir koji nije upozorio. Isti razlog zbog kojeg to piše i kopija.
		-->
		<p v-if="slip.test" class="mark">{{ label('TICKETTEST') }}</p>

		<!--
			KOPIJA TO KAŽE PRIJE SVEGA OSTALOG

			Blagajnik ispisuje kopiju kad je papir izgubljen ili nečitljiv. Ako kopija ne piše da je
			kopija, ona **jest** drugi original — a dva papira iste vrijednosti odu na dvije blagajne.
		-->
		<p v-if="slip.copy" class="mark">{{ label('TICKETCOPY') }}</p>

		<p v-if="madeAt" class="where">{{ label('TICKETMADEAT') }}: {{ madeAt }}</p>

		<!--
			ZAGLAVLJE: ŠIFRA, KONTROLNI KOD I VRIJEME

			Tri stupca jer se čitaju zajedno: šifra identificira listić, kontrolni kod ga naplaćuje, a
			vrijeme kaže kad je uplaćen — i to je sve što treba prije nego se pogleda igra.

			**Kopija kontrolni kod nema**, ni ovdje ni u crtičnom kodu: on je ono čime se listić
			isplaćuje. Na njegovu mjestu stoji zašto.
		-->
		<table class="head3">
			<tr>
				<td>{{ label('TICKETID') }}</td>
				<td>{{ slip.copy ? '' : label('CONTROLCODE') }}</td>
				<td class="r">{{ label('DATE') }}</td>
			</tr>
			<tr class="b">
				<td>{{ slip.code }}</td>
				<td>{{ slip.copy ? '' : slip.codeControl }}</td>
				<td class="r">{{ slip.placedAtDate }}</td>
			</tr>
			<tr>
				<td colspan="2">{{ slip.copy ? label('TICKETCOPYNOTE') : '' }}</td>
				<td class="r">{{ slip.placedAtTime }}</td>
			</tr>
		</table>

		<hr>

		<!--
			IGRA: IME, FORMAT I KOJE IZVLAČENJE

			Format (`12/66`) stoji uz ime jer je jedino po čemu su `Češki Keno` (12/60) i
			`Rychlé kačky` (12/66) različite oklade — vidi „Format igre stoji uz ime" u CLAUDE.md.
		-->
		<table>
			<tr class="b">
				<td>{{ gameLine }}</td>
				<td class="r">{{ drawAt }}</td>
			</tr>
		</table>

		<p v-if="slip.drawId" class="draw">{{ slip.drawId }}</p>

		<hr>

		<!--
			ODIGRANI BROJEVI, KAO KUGLICE

			`print-color-adjust: exact` nije ukras: preglednik po zadanom **ne ispisuje pozadine**, pa
			bi bez toga ostali bijeli brojevi na bijelom — dakle listić s kojeg se ne vidi što je
			odigrano. Ljuska ispisuje s `printBackground: true` i bez toga, ali papir iz preglednika
			ide na isti pisač i mora izgledati isto.
		-->
		<!--
			THE MARKET STANDS BESIDE THE NUMBERS, AND THAT IS THE WHOLE REASON FOR THIS BLOCK

			A slip on `notdrawn` is otherwise indistinguishable from one on `drawn` - the same board,
			the same numbers, the same amount - so a player who bet on the numbers that will **not**
			come out was handed a paper that reads as a bet on them coming out. When the argument comes
			to the counter afterwards, the paper has nothing to answer with.

			It is printed even when there is one market: the one that needs saying most is the one that
			looks like the ordinary one. And the market stands **per selection**, so one ticket may
			carry both - hence a row per market rather than one label above everything.
		-->
		<div v-for="one of (slip.markets || [])" :key="one.market" class="market">
			<p class="muted market-name">{{ label(one.label) }}</p>

			<!--
				A BET ON THE SUM HAS NO BALLS, AND THE TWO WORDS ARE THE WHOLE READING OF IT

				`overunder` (163) is not a number off the board: what was played is a **side** and the
				line it is read against. A paper that prints a ball with `under` written in it says
				neither - and one that prints nothing (which is what happened while the group was built
				out of numbers alone) leaves the player holding a slip that does not say what was bet.
			-->
			<p v-if="one.picks === 'outcomes'" class="nums">
				<span class="side">{{ label(one.outcomes[0]) }}</span>
				<span v-if="one.line" class="muted line">{{ label('LOTTOLINE') }} {{ one.line }}</span>
			</p>

			<p v-else class="nums">
				<span v-for="num of one.numbers" :key="num" class="ball">{{ num }}</span>
			</p>
		</div>

		<hr>

		<!--
			VRSTA, SUSTAV I KOLIKO JE BROJEVA

			`Kombinacije` je sustav kakav je biblioteka odigrala (`3/6`), a ne broj odabranih brojeva:
			fiksiran broj je u svakoj kombinaciji i ne broji se među slobodne. Na običnom listiću
			sustava nema, pa ondje stoji crtica.
		-->
		<table class="three">
			<tr>
				<td>{{ label('TICKETKIND') }}</td>
				<td>{{ label('COMBINATIONS') }}</td>
				<td class="r">{{ label('NUMBERCOUNT') }}</td>
			</tr>
			<tr class="b">
				<td>{{ kind }}</td>
				<td>{{ slip.system || '—' }}</td>
				<!-- a bet on the sum plays no numbers, and a nought there reads as a count -->
				<td class="r">{{ (slip.numbers || []).length || '—' }}</td>
			</tr>
		</table>

		<!--
			TABLICA MOGUĆIH DOBITAKA

			Sustav plaća po tome **koliko** je brojeva izašlo, pa jedan iznos ne kaže ništa. Lijevo je
			ono što bi igrač dobio na ruke, u zagradi dobitak prije poreza — porez računa
			`lib/TicketTax.hitTable`, istim računom kojim je izračunat i veliki iznos niže.

			Na običnom listiću je nema: ondje svi brojevi moraju izaći, pa je ishod jedan i već piše
			dolje.
		-->
		<template v-if="(slip.hitTable || []).length > 0">
			<hr>

			<p class="mid">{{ label('WINTABLE') }}:</p>

			<p v-for="row of slip.hitTable" :key="row.hits" class="mid">
				{{ row.hits }}: {{ money(row.net) }} {{ slip.currency }} ({{ money(row.gross) }} {{ slip.currency }})
			</p>
		</template>

		<hr>

		<!--
			ŠTO JE PLAĆENO: UPLATA, NAKNADA, ULOG

			Tri broja koja se moraju vidjeti zajedno, jer je razlika među njima točno ono što se s
			uplate skinulo: naknada operatera i, kad ga ima, porez na uplatu ispod.
		-->
		<table class="three">
			<tr>
				<td>{{ label('PAYIN') }}</td>
				<td>{{ label('HANDLINGFEE') }}</td>
				<td class="r">{{ label('STAKE') }}</td>
			</tr>
			<tr class="b">
				<td>{{ money(slip.stake) }} {{ slip.currency }}</td>
				<td>{{ money(slip.handlingFee) }} {{ slip.currency }}</td>
				<td class="r">{{ money(slip.stakeNet) }} {{ slip.currency }}</td>
			</tr>
		</table>

		<!--
			POREZ NA UPLATU, KAD GA IMA

			Bez ovog retka igrač drži papir na kojem piše deset, a dobitak se računa po devet i pol — i
			nema po čemu vidjeti zašto. Stopa ide u zagradu dok je jedna, a u vlastite retke kad ih je
			više: progresivni porez nije jedan postotak od svega nego zbroj odsječaka, a zbroj koji se
			ne da provjeriti je zbroj kojem se ne vjeruje.
		-->
		<table v-if="slip.tax">
			<tr>
				<td>{{ label('TAXONPAYIN') }}{{ onePercent ? ` (${onePercent})` : '' }}</td>
				<td class="r">{{ money(slip.tax.amount) }} {{ slip.currency }}</td>
			</tr>
			<tr v-for="(rate, index) of manyRates" :key="index">
				<td class="sub">{{ rate.percent }} / {{ money(rate.on) }}</td>
				<td class="r sub">{{ money(rate.amount) }} {{ slip.currency }}</td>
			</tr>
		</table>

		<!--
			TEČAJ, BROJ KOMBINACIJA I NAJMANJA ISPLATA

			Sve troje je o tome što listić **može** donijeti, pa stoji zajedno i dolazi iz jednog bloka
			(`TicketShape.estimateOf`) — čitano s tri mjesta, ta se tri broja mogu raziči.
		-->
		<table v-if="hasEstimate" class="three">
			<tr>
				<td>{{ label('TOTALODDS') }}</td>
				<td>{{ label('COMBCOUNT') }}</td>
				<td class="r">{{ label('MINPAYOUT') }}</td>
			</tr>
			<tr class="b">
				<td>{{ odds }}</td>
				<td>{{ slip.estimate.combinationCount }}</td>
				<td class="r">{{ money(slip.estimate.minPayout) }} {{ slip.currency }}</td>
			</tr>
		</table>

		<hr>

		<!--
			NAJVEĆA ISPLATA, VELIKIM SLOVIMA

			Jedini broj na papiru koji igrač traži pogledom, i jedini koji ne smije biti kriv: ono što
			bi se **na ruke** dobilo, dakle dobitak umanjen za porez. Zato ispod stoje i ta dva broja,
			da se razlika da provjeriti bez ičije riječi.
		-->
		<p class="big-label">{{ label('PAYOUT') }}</p>
		<p class="big">{{ money(payout) }} {{ slip.currency }}</p>

		<table v-if="hasEstimate">
			<tr>
				<td>{{ label('WIN') }}</td>
				<td class="r">{{ label('TAXONPAYOUT') }}</td>
			</tr>
			<tr class="b">
				<td>{{ money(slip.estimate.win) }} {{ slip.currency }}</td>
				<td class="r">{{ money(slip.estimate.taxOnPayout) }} {{ slip.currency }}</td>
			</tr>
		</table>

		<hr>

		<!--
			CRTIČNI KOD ILI QR, NACRTAN OVDJE

			Pravokutnici kao elementi, ne `v-html` sa SVG stringom: komponenta nema `v-html` nigdje i
			ne dobiva ga zbog koda. `widths` i `matrix` vraćaju **podatke** (širine traka, mrežu
			modula), a crtanje je Vue.

			`id` ostaje: ljuska ga traži kad ubacuje kod u rezervni papir, a `pretpregled` u njega
			upisuje svoju bilješku.
		-->
		<div id="slip-barcode" class="bar">
			<svg
				v-if="bars.length > 0"
				:width="barsWidth"
				:height="barsHeight"
				:viewBox="`0 0 ${barsWidth} ${barsHeight}`"
				shape-rendering="crispEdges"
			>
				<rect :width="barsWidth" :height="barsHeight" fill="#fff" />
				<g fill="#000">
					<rect v-for="(one, index) of bars" :key="index" :x="one.x" y="0" :width="one.width" :height="barsHeight" />
				</g>
			</svg>

			<svg
				v-else-if="modules.length > 0"
				:width="qrSize"
				:height="qrSize"
				:viewBox="`0 0 ${qrSize} ${qrSize}`"
				shape-rendering="crispEdges"
			>
				<rect :width="qrSize" :height="qrSize" fill="#fff" />
				<g fill="#000">
					<rect
						v-for="(one, index) of modules"
						:key="index"
						:x="one.x"
						:y="one.y"
						:width="QR_MODULE"
						:height="QR_MODULE"
					/>
				</g>
			</svg>
		</div>

		<p class="code">{{ slip.code }}</p>

		<!--
			Podnožje operatera — ono što tvrtka stavlja na dno papira.

			Ispod koda, jer iznad njega stoji sve po čemu se listić naplaćuje; ovo je posljednje što se
			čita i ničemu ne smije stajati na putu.
		-->
		<p v-if="slip.branding && slip.branding.footer" class="foot">{{ slip.branding.footer }}</p>
	</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import type { PrintSlip } from '@/models/betslip.ts';
import { widths as barWidths } from '@lib/shared/Code128.ts';
import { matrix as qrMatrix } from '@lib/shared/QrCode.ts';
import { codeText, formatOf } from '@lib/shared/TicketCode.ts';

/** the narrowest bar and the height of the barcode, in px at 96 dpi; a thermal head resolves this */
const BAR_MODULE = 2;
const BAR_HEIGHT = 56;

/** one QR module, and the quiet zone the specification asks for - a scanner needs both */
const QR_MODULE = 3;
const QR_QUIET = 4;

/**
 * The slip for the printer
 *
 * Without a `setup`, without a store and without `$t`: this component is drawn by the print page
 * (`views/print/PrintSlip.vue`), which the shell and the browser both load, and by `printSlip.js`
 * when the paper has to be rendered into a string instead. Everything it needs comes through props.
 */
export default defineComponent({
	name: 'SlipPrint',

	props:
	{
		/**
		 * The slip, as `slipFromTicket` assembles it.
		 *
		 * `PropType<PrintSlip>` and not a bare `Object`: with `Object` every `slip.x` below and in
		 * the template is `any`, so the paper - the one place a wrong field cannot be corrected -
		 * was the least checked thing in the project.
		 */
		slip: { type: Object as PropType<PrintSlip>, required: true },

		/**
		 * The translations of the labels.
		 *
		 * A map, not `$t`: the component is drawn outside the application, so there is no i18n instance. The call
		 * site reads them out of the application and hands them over here - the paper thereby stays in the language
		 * of the branch, and the component does not depend on anything outside itself.
		 */
		labels: { type: Object, default: () => ({}) },

		/**
		 * The width of the paper in millimetres.
		 *
		 * A setting of the **machine** (`--paperWidthMm` in the shell), but `@page size` is CSS - and CSS is part of
		 * the layout. That is why the number comes here, and the rule is written by the component: the style of the slip thereby stands in
		 * one place instead of having its classes in the shell and its markup here.
		 */
		paperWidthMm: { type: Number, default: 80 },
	},

	data()
	{
		/** the module size is read in the template, so it has to be on the instance */
		return { QR_MODULE };
	},

	computed:
	{
		/**
		 * THE CODE IS DRAWN HERE, AND THAT IS WHY THE PAPER OUT OF A BROWSER IS USABLE
		 *
		 * The shell used to insert the finished SVG, so it was the only thing that could produce one:
		 * a slip printed out of a browser came out with an empty box where the code belongs, and the
		 * cashier had to read the code out and type it. The generators are shared now
		 * (`lib/shared/Code128.ts`, `lib/shared/QrCode.ts`), and what goes **into** the code is shared too
		 * (`lib/shared/TicketCode.ts`) - the same file the scanner's reader comes from.
		 *
		 * Bars, or an empty list when this machine prints a QR instead.
		 */
		bars()
		{
			const me = this;

			if (me.slip.code == null || me.slip.code === '' || formatOf(me.slip) === 'qr')
			{
				return [];
			}

			const quiet = 10 * BAR_MODULE;
			const out: Array<{ x: number; width: number }> = [];
			let x = quiet;

			/**
			 * Even indices are bars, odd ones the gaps between them - the widths come as one
			 * alternating sequence, which is how Code 128 is defined.
			 */
			barWidths(codeText(me.slip)).forEach((width, index) =>
			{
				if (index % 2 === 0)
				{
					out.push({ x, width: width * BAR_MODULE });
				}

				x += width * BAR_MODULE;
			});

			return out;
		},

		/** the width the bars ended at, plus the quiet zone on the far side */
		barsWidth()
		{
			const me = this;

			if (me.bars.length === 0)
			{
				return 0;
			}

			const last = me.bars[me.bars.length - 1];

			return last.x + last.width + (10 * BAR_MODULE);
		},

		barsHeight()
		{
			return BAR_HEIGHT;
		},

		/** and the QR, as one square per dark module */
		modules()
		{
			const me = this;

			if (me.slip.code == null || me.slip.code === '' || formatOf(me.slip) !== 'qr')
			{
				return [];
			}

			const grid = qrMatrix(codeText(me.slip));
			const out: Array<{ x: number; y: number }> = [];

			for (let row = 0; row < grid.length; row += 1)
			{
				for (let column = 0; column < grid.length; column += 1)
				{
					if (grid[row][column] === 1)
					{
						out.push({
							x: (column + QR_QUIET) * QR_MODULE,
							y: (row + QR_QUIET) * QR_MODULE,
						});
					}
				}
			}

			return out;
		},

		qrSize()
		{
			const me = this;
			const grid = (me.modules.length > 0) ? qrMatrix(codeText(me.slip)).length : 0;

			return (grid + (QR_QUIET * 2)) * QR_MODULE;
		},

		/** whether the operator has entered anything at all - without it the paper starts at the game */
		hasBranding()
		{
			const one = this.slip.branding;

			return (one != null && (one.logo !== '' || one.header !== ''));
		},

		/**
		 * The style of the paper.
		 *
		 * The units are millimetres and points, not `rem`: paper has no root font, and a printer has a physical
		 * width. The `@page size` comes out of the width the shell says - a setting of the machine, but the rule is
		 * CSS, so it is written by the side that holds the layout.
		 */
		css()
		{
			const me = this;

			return `
@page { size: ${me.paperWidthMm}mm auto; margin: 0; }
* { box-sizing: border-box; }
body { margin: 0; padding: 3mm; width: ${me.paperWidthMm}mm; font: 10pt/1.25 "Courier New", monospace; color: #000; }
.mark { font-size: 12pt; font-weight: bold; text-align: center; letter-spacing: 1pt; margin: 1mm 0; }
.where { font-size: 9pt; margin: 1mm 0; }
.draw { font-weight: bold; margin: 0; }
.mid { text-align: center; margin: 0.5mm 0; }
.code { font-size: 14pt; font-weight: bold; text-align: center; letter-spacing: 1pt; margin: 1mm 0 0; }
.big-label { text-align: center; margin: 1mm 0 0; }
.big { font-size: 20pt; font-weight: bold; text-align: center; margin: 0 0 1mm; }
/*
 * Ime marketa iznad svojih kuglica, i ne kao red tablice: papir je 80 mm i tablica bi ga podijelila
 * na stupce koje kuglice ne mogu ispuniti. Ovako je to jedna uska linija teksta pa niz kuglica ispod
 * nje, a kad su dva marketa jedan ispod drugoga.
 */
.market { margin: 0 0 2mm; }
.market-name { margin: 0 0 1mm; text-transform: uppercase; letter-spacing: 0.4px; }

.nums { text-align: center; margin: 2mm 0; }
.ball { display: inline-block; width: 8mm; height: 8mm; line-height: 8mm; margin: 0.5mm;
    border-radius: 50%; background: #000; color: #fff; font-weight: bold; font-size: 11pt; text-align: center;
    -webkit-print-color-adjust: exact; print-color-adjust: exact; }

/* a bet on the sum: a word, not a ball - and the line right beside it, because one says nothing without the other */
.side { display: inline-block; padding: 0.8mm 2mm; margin: 0.5mm; border: 0.3mm solid #000;
    font-weight: bold; font-size: 11pt; text-transform: uppercase; }
.line { display: inline-block; margin-left: 2mm; font-size: 8pt; }
table { width: 100%; border-collapse: collapse; }
td { padding: 0.5mm 0; vertical-align: top; }
tr.b td { font-weight: bold; }
.three td { width: 33%; }
.three td:nth-child(2) { text-align: center; }
.head3 td { font-size: 9pt; }

/* brojevi se ne prelamaju, labele smiju - datum u trecini papira inace padne u tri retka;
   ograniceno na brojcane tablice, jer se ime igre prelomiti mora ili izade s papira */
.head3 tr.b td, .three tr.b td { white-space: nowrap; }
.head3 td:last-child { white-space: nowrap; }
.r { text-align: right; }
.sub { font-size: 9pt; padding-left: 3mm; }
hr { border: 0; border-top: 1px dashed #000; margin: 1.5mm 0; }
.bar { text-align: center; }
.bar svg { max-width: 100%; height: auto; }
.brand { text-align: center; margin: 0 0 1mm; }
.brand .logo { max-width: 100%; max-height: 20mm; }
.head, .foot { font-size: 9pt; text-align: center; white-space: pre-line; margin: 1mm 0 0; }
.foot { margin: 2mm 0 0; }
`.trim();
		},

		/** the game with its format beside it, as `Rychlé kačky - 12/66` */
		gameLine()
		{
			const me = this;

			return [me.slip.game, me.slip.format].filter(Boolean).join(' - ');
		},

		/** where the slip was made: the machine and the address of the shop */
		madeAt()
		{
			const me = this;

			return [me.slip.terminalCode || me.slip.terminal, me.slip.address].filter(Boolean).join(', ');
		},

		/** the date and the time of the draw in one line; empty when there is no draw */
		drawAt()
		{
			const me = this;

			return [me.slip.drawAt, me.slip.drawTime].filter(Boolean).join(' ');
		},

		/** the product, as a word rather than a code (`LOTTO` -> `Loto`) */
		kind()
		{
			const me = this;

			return (me.slip.product) ? me.label(me.slip.product) : '';
		},

		/** whether there is anything to print about what the ticket can bring */
		hasEstimate()
		{
			const one = this.slip.estimate;

			return (one != null && one.payout != null);
		},

		/**
		 * The odds, with the grouping the amount has.
		 *
		 * A total of `96200` reads as a phone number without it, and this is the one figure on the
		 * paper that regularly has five digits. Nothing is computed - the separator is put in, and the
		 * decimals are left exactly as the ticket carries them (rule 14).
		 */
		odds()
		{
			const me = this;
			const text = String((me.slip.estimate != null && me.slip.estimate.odds != null) ? me.slip.estimate.odds : '');

			if (!/^-?\d+(\.\d+)?$/.test(text))
			{
				return text;
			}

			const [whole, fraction = ''] = text.split('.');
			const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

			return (fraction === '') ? grouped : `${grouped},${fraction}`;
		},

		/** what would be handed over: the estimate when there is one, the plain possible win otherwise */
		payout()
		{
			const me = this;

			return (me.hasEstimate) ? me.slip.estimate.payout : me.slip.possibleWin;
		},

		/** the rate in brackets beside the label, but only while there is exactly one */
		onePercent()
		{
			const me = this;
			const rates = (me.slip.tax != null) ? me.slip.tax.rates : [];

			return (rates.length === 1) ? rates[0].percent : '';
		},

		/** and its own line per slice once there is more than one - a progressive tax has to add up */
		manyRates()
		{
			const me = this;
			const rates = (me.slip.tax != null) ? me.slip.tax.rates : [];

			return (rates.length > 1) ? rates : [];
		},
	},

	methods:
	{
		/** the label out of the map handed over; without it the key stays, as everywhere in the project */
		label(key: any)
		{
			const me = this;

			return (me.labels[key] != null && me.labels[key] !== '') ? me.labels[key] : key;
		},

		/**
		 * The amount for printing, without floating point (rule 14).
		 *
		 * Here it is only **displayed** - two decimals, as text. Nothing is computed: the amount arrives as a
		 * decimal string or as a number as the betslip library gives it, and as such it goes onto the paper.
		 */
		money(value: any)
		{
			if (value == null || value === '')
			{
				return '';
			}

			const text = String(value);

			if (!/^-?\d+(\.\d+)?$/.test(text))
			{
				return text;
			}

			const [whole, fraction = ''] = text.split('.');

			return `${whole},${`${fraction}00`.slice(0, 2)}`;
		},
	},
});
</script>
